'use client';

import heroBackground from '@/public/home/heroBackground.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import ChipText from '@/components/global/ChipText';
import heroCard from '@/public/home/heroCard.png';
import manInTheOffice from '@/public/home/challengeImage.png';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import AutoScroll from 'embla-carousel-auto-scroll';

interface ITrustedProps {
  className?: string;
  chipText: string;
  title: PortableTextBlock[];
  companies: {
    image: GROQImage;
  }[];
  description: string;
}

export default function Trusted({
  className,
  chipText,
  title,
  companies,
  description,
}: ITrustedProps) {

  return (
    <section className={`w-full flex flex-col items-center py-[13rem] lg:py-[18.5rem] ${className}`}>
      <div className="container flex flex-col gap-[7rem] lg:gap-[10rem] items-center justify-center w-full lg:max-w-[168.75rem] relative overflow-visible">
        <div className="flex flex-col gap-[2rem] w-full items-center">
          {chipText &&
            <ChipText text={chipText} color="purple" />
          }
          <div className="w-full items-center richtext richtext-center prose-headings:text-[4rem] lg:prose-headings:text-[6rem] prose-headings:mb-0">
            <PortableText value={title} components={richTextComponent} />
          </div>
        </div>

        <div className="flex flex-wrap gap-[3rem] lg:gap-[7.38rem] lg:inline-grid lg:grid-cols-5 justify-between lg:justify-center w-full">
          {companies?.length > 0 && companies?.map((company, index) => (
            <motion.div key={index}
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1], delay: index * 0.075 }}
              viewport={{ once: true }}
              className="w-fit"
            >
              <Image
                src={company.image.src}
                width={company.image.width}
                height={company.image.height}
                quality={100}
                className="w-fit h-[4rem] lg:h-[8.625rem]"
                alt=""
              />
            </motion.div>
          ))}
        </div>

        <motion.p className="text-[3rem] lg:text-[3rem] font-[400] text-[#072922] font-inter-tight leading-[140%] mb-0 lg:mt-[2rem]"
          initial={{ opacity: 0, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 1.1 }}
          viewport={{ once: true }}
        >
          {description}
        </motion.p>
      </div>
    </section>
  );
}
